import styled, { css } from 'foundations-theming-specialty/utils';
import useDisclosure from '../../hooks/useDisclosure';
import TrackedButton from '../TrackedButton';
import TrackedLink from '../TrackedLink';
import VerticalNavSecondaryMenu from './VerticalNavSecondaryMenu';
import { useMemo } from 'react';
import VerticalNavMenuItemIcon from './VerticalNavMenuItemIcon';
import { BREEZE_AI_VERTICAL_NAV_ITEM_ID, ICON_ONLY_NAV_DELAY_MS, PRIMARY_NAV_ITEM_MIN_HEIGHT, VERTICAL_NAV_LIST_ITEM_MARGIN } from './constants';
import { MenuItemLevel } from '../../constants';
import useVerticalNavFocus from './hooks/useVerticalNavFocus';
import convertToAbsolutePath from '../../utils/convertToAbsolutePath';
import { ButtonWithDemoExitConfirmation } from '../DemoExitConfirmation/ButtonWithDemoExitConfirmation';
import { createPortal } from 'react-dom';
import VerticalNavMenuItemHitbox from './VerticalNavMenuItemHitbox';
import useVerticalNavSecondaryMenu from './hooks/useVerticalNavSecondaryMenu';
import { NAV_ITEM_BOOKMARKS_GROUP_ID } from '../../constants/Bookmarks';
import VerticalNavMenuItemTooltip from './VerticalNavMenuItemTooltip';
import useIsVerticalNavPinned from '../../hooks/useIsVerticalNavPinned';
import debounce from 'hs-lodash/debounce';
import withTransientStyleProps from '../../utils/withTransientStyleProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const arrow = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11.5 21.1' %3E%3Cpath d='M2 2l7.5 8.5-7.4 8.6' fill='none' stroke='%23f5f8fa' stroke-linecap='round' stroke-linejoin='round' stroke-width='4'%3E%3C/path%3E%3C/svg%3E")`;
const Li = styled.li.withConfig({
  displayName: "VerticalNavMenuItem__Li",
  componentId: "oft1jq-0"
})([""]);
export const StyledLi = styled(withTransientStyleProps(Li))(({
  theme,
  withDividerAbove,
  withDividerBelow,
  $isVerticalNavPinned
}) => {
  return css(["margin:0 ", ";border-radius:6px;position:relative;", " & > a:hover,& > button:hover,&.isHighlighted > a,&.isHighlighted > button{color:", ";span{color:", ";}svg{fill:", ";}}& > a:hover,& > button:hover,&.isHighlighted > a,&.isHighlighted > button:hover{&::before{background:", ";border:none;}}&.isHighlighted > a,&.isHighlighted > button{&::before{background:", ";}}", " &.isHighlighted > button:hover::after{opacity:1;}& > button,& > a{display:flex;font-weight:300;flex-direction:row;justify-content:", ";align-items:center;flex-shrink:0;-webkit-font-smoothing:auto;-moz-osx-font-smoothing:auto;width:100%;border:0;border-radius:6px;min-height:", ";background:none;position:relative;padding:", ";&:hover{text-decoration:none;svg{fill:", ";}span{color:", ";}}svg{fill:", ";width:16px;min-width:16px;", "}span{color:", ";font-weight:300;text-align:left;font-size:14px;line-height:normal;display:block;padding:0;overflow-wrap:break-word;hyphens:manual;}}& > a::before,& > button::before{content:'';position:absolute;top:6px;bottom:6px;left:12px;width:calc(100% - 24px);background:", ";border-radius:6px;opacity:0;z-index:-1;}& > a:hover::before,& > button:hover::before,&.isHighlighted > button::before,&.isHighlighted > a::before,&.isExpanded > button::before,&.isExpanded > a::before{opacity:1;}"], VERTICAL_NAV_LIST_ITEM_MARGIN, withDividerAbove || withDividerBelow ? css(["&::", "{content:'';margin:12px 24px;padding:0;background:", ";border:0;height:1px;width:calc(100% - 48px);display:block;}"], withDividerAbove ? 'before' : 'after', theme.color['global-shell-border-default']) : '', theme.color['global-shell-text-nav-item-hover'], theme.color['global-shell-text-nav-item-hover'], theme.color['global-shell-text-nav-item-hover'], theme.color['global-shell-fill-nav-item-hover'], theme.color['global-shell-fill-nav-item-selected'], !$isVerticalNavPinned ? '' : css(["&.isExpanded > button::after,& > button:hover::after{color:", ";position:absolute;opacity:1;content:", ";width:6px;right:24px;}"], theme.color['global-shell-text-nav-item-hover'], arrow), !$isVerticalNavPinned ? 'center' : 'start', PRIMARY_NAV_ITEM_MIN_HEIGHT, $isVerticalNavPinned ? '14px 24px' : '14px 12px', theme.color['global-shell-icon-nav-item-hover'], theme.color['global-shell-text-nav-item-hover'], theme.color['global-shell-icon-nav-item-default'], !$isVerticalNavPinned ? '' : css(["margin-right:8px;"]), theme.color['global-shell-text-nav-item-default'], theme.color['global-shell-fill-nav-item-hover']);
});
StyledLi.displayName = 'StyledLi';
export const VerticalNavMenuItemContent = ({
  id,
  label
}) => {
  const isVerticalNavPinned = useIsVerticalNavPinned();
  const iconTitle = isVerticalNavPinned ? undefined : label;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(VerticalNavMenuItemIcon, {
      id: id,
      title: iconTitle
    }), isVerticalNavPinned ? /*#__PURE__*/_jsx("span", {
      children: label
    }) : null]
  });
};
const getVerticalNavMenuItemClassName = ({
  isExpanded,
  shouldHighlight
}) => {
  const classes = ['primary-nav-menu-item'];
  if (shouldHighlight) {
    classes.push('isHighlighted');
  }
  if (isExpanded) {
    classes.push('isExpanded');
  }
  return classes.join(' ');
};
const VerticalNavMenuItem = ({
  id,
  onClick,
  label,
  path,
  isSubmenuOpen,
  secondaryNavItems,
  onKeyDown,
  onMouseEnter,
  shouldHighlight,
  setIsMouseMovingTowardsSubmenu,
  isBookmarksMenu,
  isTooltipOpen,
  closeTooltip,
  isASecondaryMenuOpen,
  isVerticalNavPinned
}) => {
  const {
    ref
  } = useVerticalNavFocus({
    id
  });
  const {
    toggleProps,
    contentProps,
    isExpanded
  } = useDisclosure({
    id,
    isExpanded: isSubmenuOpen
  });
  const isBookmarksItem = id === NAV_ITEM_BOOKMARKS_GROUP_ID;
  const isBreezeAiItem = id === BREEZE_AI_VERTICAL_NAV_ITEM_ID;
  const isSecondaryMenuOpen = isExpanded && !!secondaryNavItems;
  const className = useMemo(() => getVerticalNavMenuItemClassName({
    isExpanded: isSecondaryMenuOpen,
    shouldHighlight
  }), [isSecondaryMenuOpen, shouldHighlight]);
  const {
    setSecondaryMenuItemRef,
    hitboxStyles,
    hitboxPath
  } = useVerticalNavSecondaryMenu({
    primaryNavItemRef: ref,
    isSecondaryMenuOpen
  });
  const onDelayedMouseEnter = debounce(onMouseEnter, ICON_ONLY_NAV_DELAY_MS);
  const onMouseEnterHandler = !isASecondaryMenuOpen ? onDelayedMouseEnter : onMouseEnter;
  if (path) {
    const href = convertToAbsolutePath(path);
    return /*#__PURE__*/_jsxs(StyledLi, {
      role: "none",
      className: className,
      shouldHighlight: shouldHighlight,
      $isVerticalNavPinned: isVerticalNavPinned,
      children: [/*#__PURE__*/_jsx(ButtonWithDemoExitConfirmation, {
        component: TrackedLink,
        role: "menuitem",
        id: id,
        href: href,
        onKeyDown: onKeyDown,
        onMouseEnter: onMouseEnterHandler,
        onMouseLeave: closeTooltip,
        innerRef: ref,
        "data-menu-item-level": MenuItemLevel.PRIMARY,
        "data-menu-item-group": id,
        "data-test-id": id,
        children: /*#__PURE__*/_jsx(VerticalNavMenuItemContent, {
          id: id,
          label: label
        })
      }), !isVerticalNavPinned && isTooltipOpen ? /*#__PURE__*/_jsx(VerticalNavMenuItemTooltip, {
        label: label,
        buttonRef: ref
      }) : null]
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(StyledLi, {
      role: "none",
      isExpanded: isExpanded,
      className: className,
      shouldHighlight: shouldHighlight,
      withDividerBelow: isBookmarksItem,
      withDividerAbove: isBreezeAiItem,
      $isVerticalNavPinned: isVerticalNavPinned,
      children: [/*#__PURE__*/_jsx(TrackedButton, Object.assign({}, toggleProps, {
        onClick: onClick,
        onKeyDown: onKeyDown,
        onMouseEnter: onMouseEnterHandler,
        role: "menuitem",
        "aria-haspopup": "true",
        ref: ref,
        "data-menu-item-level": MenuItemLevel.PRIMARY,
        "data-menu-item-group": id,
        children: /*#__PURE__*/_jsx(VerticalNavMenuItemContent, {
          id: id,
          label: label
        })
      })), isSecondaryMenuOpen ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(VerticalNavSecondaryMenu, Object.assign({
          ref: setSecondaryMenuItemRef,
          secondaryNavItems: secondaryNavItems,
          isBookmarksMenu: isBookmarksMenu,
          menuTitle: label
        }, contentProps)), /*#__PURE__*/createPortal( /*#__PURE__*/_jsx(VerticalNavMenuItemHitbox, Object.assign({
          setIsMouseMovingTowardsSubmenu: setIsMouseMovingTowardsSubmenu,
          hitboxPath: hitboxPath
        }, hitboxStyles.current)), document.body)]
      }) : null]
    })
  });
};
export default VerticalNavMenuItem;